import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");

    // Helper: strip the hash from URL BEFORE navigating to avoid re-render loop
    const stripHashAndGo = (path) => {
      try {
        window.history.replaceState(null, "", path);
      } catch (e) {
        // ignore
      }
      navigate(path, { replace: true });
    };

    if (!sessionId) {
      stripHashAndGo("/");
      return;
    }

    const exchangeSession = async () => {
      try {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const res = await fetch(`${API}/auth/session?session_id=${sessionId}`, {
          credentials: "include",
        });

        if (res.ok) {
          try {
            const userData = await res.json();
            setUser(userData);
          } catch {
            // Body may have been consumed by platform interceptor - cookie is still set
          }
          // Always verify auth via cookie to ensure state is consistent
          await checkAuth();
          stripHashAndGo("/dashboard");
        } else {
          // Cookie might still have been set - try checkAuth as fallback
          await checkAuth();
          stripHashAndGo("/dashboard");
        }
      } catch (e) {
        console.error("Auth exchange error:", e);
        // Last resort: try checkAuth in case cookie was set despite the error
        try { await checkAuth(); } catch {}
        setErrorMsg("Errore durante l'accesso. Riprova.");
        // Fallback redirect to landing after a short delay so the user sees the message
        setTimeout(() => stripHashAndGo("/"), 1500);
      }
    };

    exchangeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Always render a visible loader / message instead of `null` to prevent
  // a blank white screen if anything goes wrong with the redirect.
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback-loading">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin h-8 w-8 border-4 border-green-700 border-t-transparent rounded-full" />
        <p className="text-sm text-slate-500">
          {errorMsg || "Completamento accesso..."}
        </p>
      </div>
    </div>
  );
}
